import {
  typedCollection,
  getCollection,
  getCollectionPage,
  orderBy,
  where,
  type CollectionPage,
  type QueryDocumentSnapshot,
} from '@/lib/firebase/firestore'
import type { CompanyMemberDocument, FilmDocument, PositionDocument } from '@/types'

const membersCol = () => typedCollection<CompanyMemberDocument>('company')
const filmsCol = () => typedCollection<FilmDocument>('films')
const positionsCol = () => typedCollection<PositionDocument>('positions')

export const companyService = {
  async getPublishedMembers(): Promise<Array<CompanyMemberDocument & { id: string }>> {
    return getCollection(membersCol(), where('isPublished', '==', true), orderBy('order', 'asc'))
  },

  async getPublishedMembersPage(
    pageSize: number,
    cursor: QueryDocumentSnapshot<CompanyMemberDocument> | null
  ): Promise<CollectionPage<CompanyMemberDocument>> {
    return getCollectionPage(
      membersCol(),
      pageSize,
      cursor,
      where('isPublished', '==', true),
      orderBy('order', 'asc')
    )
  },

  async getHireableMembers(): Promise<Array<CompanyMemberDocument & { id: string }>> {
    const all = await this.getPublishedMembers()
    return all.filter((m) => m.availableForHire === true)
  },

  async getMemberBySlug(slug: string): Promise<(CompanyMemberDocument & { id: string }) | null> {
    const results = await getCollection(
      membersCol(),
      where('slug', '==', slug),
      where('isPublished', '==', true)
    )
    return results[0] ?? null
  },

  async getActivePositions(): Promise<Array<PositionDocument & { id: string }>> {
    return getCollection(positionsCol(), where('isActive', '==', true), orderBy('order', 'asc'))
  },

  async getFilmsByMemberSlug(slug: string): Promise<{
    choreographerFilms: Array<FilmDocument & { id: string }>
    castFilms: Array<FilmDocument & { id: string }>
    crewFilms: Array<FilmDocument & { id: string }>
  }> {
    const allFilms = await getCollection(
      filmsCol(),
      where('isPublished', '==', true),
      orderBy('seasonNumber', 'desc'),
      orderBy('episodeNumber', 'asc')
    )
    return {
      choreographerFilms: allFilms.filter((f) => f.choreographers.some((c) => c.slug === slug)),
      castFilms: allFilms.filter((f) => f.cast.some((c) => c.slug === slug)),
      crewFilms: allFilms.filter((f) => f.crew.some((c) => c.slug === slug)),
    }
  },
}
